import type { Messages } from '../i18n/messages';

/**
 * usvc-migrations module message catalog (Task 7 — presentational Runs tab
 * components). Server/dynamic data (migration file names, class names,
 * domains, log messages, run statuses/types-as-data, timestamps, counts) is
 * never keyed here — those render as raw values or are interpolated via
 * `{param}`. Only static chrome (tab titles, column headers, filter labels,
 * empty states, button labels) is keyed.
 */
export const usvcMigrationsMessages: Messages = {
  // ---- Tab titles for the two "Migrations" catalog-group members (see
  // UsvcMigrationsContent.tsx / app-config.yaml's page:catalog/entity
  // groups) ----
  'usvcMigrations.tab.runs': { en: 'Runs', fun: 'Run Log' },
  'usvcMigrations.tab.runMigration': {
    en: 'Run migration',
    fun: 'Cast Migration',
  },

  // ---- RunMigrationForm.tsx / RunMigrationPanel.tsx ----
  'usvcMigrations.runMigration.title': {
    en: 'Run an on-demand migration',
    fun: 'Cast an on-demand migration',
  },
  'usvcMigrations.runMigration.description': {
    en: 'Pick an environment, a usvc-migrations branch and a migration file to dispatch.',
    fun: 'Pick an environment, a usvc-migrations branch and a migration file to cast.',
  },
  'usvcMigrations.runMigration.env.label': {
    en: 'Environment',
    fun: 'Environment',
  },
  'usvcMigrations.runMigration.env.placeholder': {
    en: 'Choose an environment',
    fun: 'Choose an environment',
  },
  'usvcMigrations.runMigration.env.dev': { en: 'Dev', fun: 'Dev' },
  'usvcMigrations.runMigration.env.staging': {
    en: 'Staging',
    fun: 'Staging',
  },
  'usvcMigrations.runMigration.env.euStaging': {
    en: 'EU staging',
    fun: 'EU staging',
  },
  'usvcMigrations.runMigration.env.prePod': {
    en: 'Pre-prod',
    fun: 'Pre-prod',
  },
  'usvcMigrations.runMigration.env.production': {
    en: 'Production',
    fun: 'Production',
  },
  'usvcMigrations.runMigration.branch.label': {
    en: 'Branch',
    fun: 'Branch',
  },
  'usvcMigrations.runMigration.branch.placeholder': {
    en: 'Choose a branch',
    fun: 'Choose a branch',
  },
  'usvcMigrations.runMigration.branch.loading': {
    en: 'Loading branches…',
    fun: 'Summoning branches…',
  },
  'usvcMigrations.runMigration.branch.refreshTooltip': {
    en: 'Refresh the branch list now',
    fun: 'Yank a fresh branch list now',
  },
  'usvcMigrations.runMigration.branch.noOptions': {
    en: 'No matching branches',
    fun: 'Nothing to summon here',
  },
  'usvcMigrations.runMigration.file.label': {
    en: 'Migration file',
    fun: 'Migration file',
  },
  'usvcMigrations.runMigration.file.placeholder': {
    en: 'Choose a migration file',
    fun: 'Choose a migration file',
  },
  'usvcMigrations.runMigration.file.noBranch': {
    en: 'Choose a branch first',
    fun: 'Choose a branch first',
  },
  'usvcMigrations.runMigration.file.loading': {
    en: 'Loading migration files…',
    fun: 'Summoning migration files…',
  },
  'usvcMigrations.runMigration.file.refreshTooltip': {
    en: 'Refresh the migration-file list now',
    fun: 'Yank a fresh migration-file list now',
  },
  'usvcMigrations.runMigration.file.noOptions': {
    en: 'No matching migration files',
    fun: 'Nothing to cast here',
  },
  'usvcMigrations.runMigration.submit.label': {
    en: 'Run migration',
    fun: 'Cast migration',
  },
  'usvcMigrations.runMigration.submit.loading': {
    en: 'Running…',
    fun: 'Casting…',
  },
  'usvcMigrations.runMigration.submit.error': {
    en: 'Could not start the migration run: {message}',
    fun: 'Could not start the migration run: {message}',
  },

  // ---- RunsPanel.tsx ----
  'usvcMigrations.refreshAll.tooltip': {
    en: 'Refresh all Migrations data (environments, runs, logs) now',
    fun: 'Yank fresh Migrations data (environments, runs, logs) now',
  },
  'usvcMigrations.env.none': {
    en: 'No environments configured',
    fun: 'No environments configured',
  },

  // ---- RunsTable.tsx ----
  'usvcMigrations.runs.empty': { en: 'No migration runs', fun: 'No runs yet' },
  'usvcMigrations.runs.loading': {
    en: 'Loading migration runs…',
    fun: 'Summoning runs…',
  },
  'usvcMigrations.runsTable.col.file': { en: 'File', fun: 'File' },
  'usvcMigrations.runsTable.col.status': { en: 'Status', fun: 'Status' },
  'usvcMigrations.runsTable.col.type': { en: 'Type', fun: 'Type' },
  'usvcMigrations.runsTable.col.domains': { en: 'Domains', fun: 'Domains' },
  'usvcMigrations.runsTable.col.executedAt': {
    en: 'Executed at',
    fun: 'Kicked off',
  },
  'usvcMigrations.runsTable.col.duration': {
    en: 'Duration',
    fun: 'Duration',
  },

  // ---- FilterBar.tsx ----
  'usvcMigrations.filter.status.label': { en: 'Status', fun: 'Status' },
  'usvcMigrations.filter.status.all': { en: 'All statuses', fun: 'Anything' },
  'usvcMigrations.filter.status.started': { en: 'Started', fun: 'Started' },
  'usvcMigrations.filter.status.completed': {
    en: 'Completed',
    fun: 'Completed',
  },
  'usvcMigrations.filter.status.failed': { en: 'Failed', fun: 'Failed' },
  'usvcMigrations.filter.runType.label': { en: 'Run type', fun: 'Run type' },
  'usvcMigrations.filter.runType.all': { en: 'All types', fun: 'Any type' },
  'usvcMigrations.filter.runType.standard': {
    en: 'Standard',
    fun: 'Standard',
  },
  'usvcMigrations.filter.runType.onDemand': {
    en: 'On-demand',
    fun: 'On-demand',
  },
  'usvcMigrations.filter.className.label': {
    en: 'Class name',
    fun: 'Class name',
  },
  'usvcMigrations.filter.className.all': {
    en: 'All classes',
    fun: 'Any class',
  },
  'usvcMigrations.filter.fileName.label': {
    en: 'Migration file',
    fun: 'Migration file',
  },
  'usvcMigrations.filter.fileName.all': {
    en: 'All files',
    fun: 'Any file',
  },
  'usvcMigrations.filter.domain.label': { en: 'Domain', fun: 'Domain' },
  'usvcMigrations.filter.domain.all': {
    en: 'All domains',
    fun: 'Any domain',
  },
  'usvcMigrations.filter.dateRange': { en: 'Date range', fun: 'Time window' },
  'usvcMigrations.filter.noOptions': { en: 'No matches', fun: 'Nothing found' },

  // ---- LogViewer.tsx — level moved here from FilterBar.tsx: it's a
  // log-line attribute, not a run attribute, so it filters the log list
  // directly instead of the runs query.
  'usvcMigrations.logs.title': { en: 'Logs', fun: 'Logs' },
  'usvcMigrations.logs.live': { en: 'Live tail', fun: 'Live tail' },
  'usvcMigrations.logs.empty': { en: 'No log lines', fun: 'Quiet in here' },
  'usvcMigrations.logs.loading': {
    en: 'Loading logs…',
    fun: 'Summoning logs…',
  },
  'usvcMigrations.logs.search': { en: 'Search logs', fun: 'Search logs' },
  'usvcMigrations.logs.level.label': { en: 'Level', fun: 'Level' },
  'usvcMigrations.logs.level.all': { en: 'All levels', fun: 'Any level' },

  // ---- EnvSwitcher.tsx ----
  'usvcMigrations.env.label': { en: 'Environment', fun: 'Environment' },
  'usvcMigrations.env.dev': { en: 'Dev', fun: 'Dev' },
  'usvcMigrations.env.staging': { en: 'Staging', fun: 'Staging' },
  'usvcMigrations.env.preprod': { en: 'Pre-prod', fun: 'Pre-prod' },
  'usvcMigrations.env.prod': { en: 'Prod', fun: 'Prod' },
};
